//	Intl-based Romanian date formatting for public blocks — replaces
//	moment + moment-timezone (moment's dual CJS/ESM entries put two copies
//	in every Vite bundle and locales registered on the wrong one; the tz
//	data builds added ~200KB besides). moment remains admin-only.
//
//	All formatting is pinned to Europe/Bucharest so SSR output does not
//	depend on the server's TZ.

const TZ = "Europe/Bucharest";

//	two cached formatters per tz: one for names (weekday/month), one for
//	zero-padded numerics — Intl can't produce both variants in one pass
const textFormatters = new Map();
const numericFormatters = new Map();

function partsOf(date, tz) {
	let text = textFormatters.get(tz);
	let numeric = numericFormatters.get(tz);
	if (!text) {
		text = new Intl.DateTimeFormat("ro-RO", {
			timeZone: tz,
			weekday: "long",
			month: "long",
		});
		numeric = new Intl.DateTimeFormat("ro-RO", {
			timeZone: tz,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hourCycle: "h23",
		});
		textFormatters.set(tz, text);
		numericFormatters.set(tz, numeric);
	}
	const map = {};
	for (const p of text.formatToParts(date)) map[p.type + "Name"] = p.value;
	for (const p of numeric.formatToParts(date)) map[p.type] = p.value;
	return map;
}

const TOKEN = /dddd|MMMM|MM|DD|D|YYYY|Y|HH|H|mm/g;

/**
 * Format a date with moment-style tokens, in Romanian.
 * Supported tokens: dddd MMMM MM DD D YYYY Y HH H mm
 * (day/month names come out lowercase, as Romanian writes them —
 * capitalize at the call site where the design wants it).
 */
export function formatRo(input, pattern, tz = TZ) {
	const date = input instanceof Date ? input : new Date(input);
	if (isNaN(date.getTime())) {
		return "";
	}
	const p = partsOf(date, tz);
	return pattern.replace(TOKEN, (t) => {
		switch (t) {
			case "dddd": return p.weekdayName;
			case "MMMM": return p.monthName;
			case "MM": return p.month;
			case "DD": return p.day;
			case "D": return String(Number(p.day));
			case "YYYY":
			case "Y": return p.year;
			case "HH": return p.hour;
			case "H": return String(Number(p.hour));
			case "mm": return p.minute;
		}
		return t;
	});
}

const RELATIVE = new Intl.RelativeTimeFormat("ro", { numeric: "auto" });

/** "acum 5 minute" / "ieri" / "acum 3 luni" — replaces moment().fromNow() */
export function fromNow(input, now = new Date()) {
	const date = input instanceof Date ? input : new Date(input);
	if (isNaN(date.getTime())) {
		return "";
	}
	const diff = (date.getTime() - now.getTime()) / 1000;
	const abs = Math.abs(diff);
	//	|| branch: sub-second diffs truncate to -0 and would read "acum 0 secunde"
	if (abs < 60) return RELATIVE.format(Math.trunc(diff) || (diff > 0 ? 1 : -1), "second");
	if (abs < 3600) return RELATIVE.format(Math.trunc(diff / 60), "minute");
	if (abs < 86400) return RELATIVE.format(Math.trunc(diff / 3600), "hour");
	if (abs < 2592000) return RELATIVE.format(Math.trunc(diff / 86400), "day");
	if (abs < 31536000) return RELATIVE.format(Math.trunc(diff / 2592000), "month");
	return RELATIVE.format(Math.trunc(diff / 31536000), "year");
}

/** signed minutes from b to a — replaces moment(a).diff(moment(b), "minutes") */
export function minutesBetween(a, b) {
	return Math.trunc((new Date(a).getTime() - new Date(b).getTime()) / 60000);
}

export function ucFirst(s) {
	return s.charAt(0).toUpperCase() + s.slice(1);
}
